import React, { useState } from 'react';

import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { UseMutationAddWidgetLayoutToReport } from 'api/mutations/addWidgetLayoutToReport.mutation';
import { useMutationSetComponent } from 'api/mutations/setComponent.mutation';
import { useMutationSetReport } from 'api/mutations/setReport.mutation';
import { useQueryGetReportList } from 'api/queries/getReportList.query';

import {
    IComponentKey,
    ILocalFilters,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ReportsListDropdown } from 'components/Dashboards/Reporting/AddToReport/components/ReportsListDropdown/ReportsListDropdown';
import { UpdateCreateWidget } from 'components/Dashboards/Reporting/reporting.types';
import { getInitialLayout } from 'components/Dashboards/Reporting/reporting.utils';

import { WidgetButton } from '../_common/widgetButtons/WidgetButton';
import classes from './AddToReport.module.scss';

interface Props {
    widgetId?: string;
    projectId: number | null | undefined;
    contractId: number | null | undefined;
    uiState?: ILocalFilters;
    componentKey: IComponentKey;
    callBack?: (widgetId: string, forceSetWidgetColumnsInReport: boolean) => void;
    shouldHide?: boolean;
    apiPayload?: object;
    widgetEndpoint?: string;
}

export const AddToReport = ({
    widgetId,
    componentKey,
    projectId,
    contractId,
    uiState,
    callBack,
    shouldHide,
    apiPayload,
    widgetEndpoint,
}: Props) => {
    //state
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(null);

    //hooks
    const { handleSnackBar } = useCustomSnackBar();
    const { ability } = useUserHook();
    const queryClient = useQueryClient();

    //queries
    const { mutateAsync: mutateAddWidgetToReport } = useMutationSetComponent();
    const { mutateAsync: addWidgetLayoutToReport } = UseMutationAddWidgetLayoutToReport();
    const { mutateAsync: mutateCreateNewReport, isLoading: isLoadingCreateNewReport } = useMutationSetReport();
    const { data: reportsList } = useQueryGetReportList({
        filter: {
            filters: [],
        },
    });

    //callbacks
    const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>) => {
        setDropdownAnchor(event.currentTarget);
        setIsDropdownOpen((isOpen) => !isOpen);
    };

    const onAddWidgetToReport = async (reportName: string, reportId: number) => {
        const widgetToBeSaved: UpdateCreateWidget = {
            id: null,
            componentKey: componentKey,
            contractId: contractId,
            projectId: projectId,
            reportId,
            params: JSON.stringify(uiState) ?? null,
            description: '',
            showNarrative: false,
            payload: JSON.stringify(apiPayload) ?? '',
            endpoint: widgetEndpoint,
        };

        await mutateAddWidgetToReport(widgetToBeSaved, {
            onSuccess: async (response) => {
                const createdWidgetId = response.id;
                callBack && callBack(createdWidgetId.toString(), true);
                await addWidgetLayoutToReport({
                    reportId,
                    widgetId: createdWidgetId.toString(),
                });
                await queryClient.invalidateQueries({
                    queryKey: ['getReport', { id: reportId }],
                });
                handleSnackBar(
                    <>
                        Component successfully added to{' '}
                        <Link
                            to={AppRoutes.buildPath(AppRoutes.DASHBOARD_REPORTING_PATH, reportId)}
                            className={classes.link}
                        >
                            {reportName}
                        </Link>
                    </>,
                    'success',
                );
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    const onCreateNewReport = async (reportName: string) => {
        const initialGridLayout = getInitialLayout();
        await mutateCreateNewReport(
            { name: reportName, gridLayout: initialGridLayout },
            {
                onSuccess: async (response) => {
                    const reportId = response.id;
                    if (reportId) {
                        //Add widget to the created report.
                        await onAddWidgetToReport(reportName, reportId);
                        await queryClient.invalidateQueries('getReportList');
                        handleSnackBar(
                            <>
                                New report created successfully{' '}
                                <Link
                                    to={AppRoutes.buildPath(AppRoutes.DASHBOARD_REPORTING_PATH, reportId)}
                                    className={classes.link}
                                >
                                    {reportName}
                                </Link>
                            </>,
                            'success',
                        );
                    }
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    if (!ability.can('view', 'ReportsView') || widgetId || shouldHide) {
        // If the user has no reporting privileges OR a widget is being viewed (reporting/pinboard mode), don't render.
        return null;
    }

    return (
        <div className={classes.AddToReport}>
            <WidgetButton icon={'add_chart'} tooltipContent={`Add to report`} onClick={handleDropdownOpen} />
            <ReportsListDropdown
                dropdownAnchorRef={dropdownAnchor}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
                reportsList={reportsList ?? []}
                onAddWidgetToReport={onAddWidgetToReport}
                onCreateNewReport={onCreateNewReport}
                isLoadingCreateNewReport={isLoadingCreateNewReport}
            />
        </div>
    );
};
